import { Button, Menu, Stack, Typography } from '@mui/material';
import { ContractPageVersion } from '@vy/api-definition';
import { useMenu } from '../../../../../hooks/use-menu';
import { VyIcon } from '@vy/ui-design-system';
import { isEmptyArray, isNotEmptyArray, isNullOrUndefined, Optional } from '@vy/utils';
import React from 'react';
import { ContractVersionMenuItems } from './ContractVersionMenuItems';
import { ContractVersionEmptyMenuItem } from './ContractVersionEmptyMenuItem';
import { useDeleteRevision } from '../../../../../hooks/requests/use-delete-revision';
import { runMutation } from '../../../../../utils/vy-mutation';
import { useQueryClient } from 'react-query';
import {
  invalidateContractsQueries,
  invalidateCustomersQueries,
  invalidateBillingRevenueQueries,
} from '../../../../../utils/query-invalidation';

type ContractVersionsSelectProps = {
  versions: ContractPageVersion[];
  versionIdFromUrl: Optional<string>;
  setVersionIdInUrl: (versionId: Optional<string>) => void;
  contractId: string;
};

export const ContractVersionsSelect: React.FC<ContractVersionsSelectProps> = ({
  versions,
  versionIdFromUrl,
  setVersionIdInUrl,
  contractId,
}) => {
  const queryClient = useQueryClient();
  const deleteRevisionMutation = useDeleteRevision({
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.includes(data.contractId),
        }),
        invalidateContractsQueries(queryClient),
        invalidateCustomersQueries(queryClient),
        invalidateBillingRevenueQueries(queryClient),
      ]);
    },
  });

  const { anchorElement, isOpen, handleMenuOpen, handleMenuClose } = useMenu();
  const getVersionIndex = (revisionId?: string) =>
    versions.findIndex((version) => version.revisionId === revisionId);

  const currentVersionIndex = isEmptyArray(versions) ? 0 : getVersionIndex(versionIdFromUrl ?? undefined);
  const currentVersionLabel = createVersionLabel(currentVersionIndex);

  const handleRevisionDelete = async (revisionId: Optional<string>) => {
    if (isNullOrUndefined(revisionId)) {
      return;
    }

    await runMutation(
      deleteRevisionMutation,
      { revisionId, contractId },
      {
        error: 'Failed to delete revision!',
        pending: 'Deleting revision...',
        success: 'Revision deleted',
      },
    );
  };

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: "center",
        gap: 1
      }}>
      <Button
        onClick={handleMenuOpen}
        startIcon={<VyIcon icon="PhasesIndicator" />}
        endIcon={<VyIcon icon="ArrowDown" />}
      >
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            lineHeight: "22px"
          }}>
          Current phase: {currentVersionLabel}
        </Typography>
      </Button>
      <Menu open={isOpen} anchorEl={anchorElement} onClose={handleMenuClose}>
        {!isNotEmptyArray(versions) ? (
          <ContractVersionEmptyMenuItem />
        ) : (
          <ContractVersionMenuItems
            versions={versions}
            versionIdFromUrl={versionIdFromUrl}
            setVersionIdInUrl={setVersionIdInUrl}
            handleMenuClose={handleMenuClose}
            getVersionLabel={createVersionLabel}
            onDeleteRevision={handleRevisionDelete}
          />
        )}
      </Menu>
    </Stack>
  );
};

function createVersionLabel(index: number) {
  return `V${index + 1}`;
}
